import { useState, useEffect } from 'react'
import {
  MessageSquare,
  User,
  Clock,
  CheckCircle,
  AlertCircle,
  XCircle,
  Send,
  Search,
  ChevronRight,
  AlertTriangle,
  HelpCircle,
  Building,
  FileText,
  Plus,
  RefreshCw,
  Book,
  X,
  BarChart3
} from 'lucide-react'
import { Select } from '../components/ui/Select'
import { PageHeader } from '../components/ui/PageHeader'
import {
  partnerAdminApi,
  type SupportTicket,
  type SupportTicketDetails,
  type SupportTicketsResponse,
  type SupportMetrics,
  type SupportTicketStatus,
  type SupportTicketPriority,
  type SupportTicketCategory
} from '../services/api'

export function Support() {
  const [activeTab, setActiveTab] = useState<'tickets' | 'metrics'>('tickets')
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)

  // Tickets state
  const [tickets, setTickets] = useState<SupportTicket[]>([])
  const [ticketsSummary, setTicketsSummary] = useState<SupportTicketsResponse['summary'] | null>(null)
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 1 })
  const [selectedTicket, setSelectedTicket] = useState<SupportTicketDetails | null>(null)
  const [loadingTicket, setLoadingTicket] = useState(false)
  const [filterStatus, setFilterStatus] = useState<SupportTicketStatus | 'all'>('all')
  const [filterPriority, setFilterPriority] = useState<SupportTicketPriority | 'all'>('all')
  const [searchTerm, setSearchTerm] = useState('')

  // New ticket modal
  const [showNewTicketModal, setShowNewTicketModal] = useState(false)
  const [newTicket, setNewTicket] = useState({
    title: '',
    description: '',
    clientId: '',
    category: 'general' as SupportTicketCategory,
    priority: 'medium' as SupportTicketPriority
  })
  const [creatingTicket, setCreatingTicket] = useState(false)

  // Reply state
  const [replyMessage, setReplyMessage] = useState('')
  const [sendingReply, setSendingReply] = useState(false)

  // Metrics state
  const [metrics, setMetrics] = useState<SupportMetrics | null>(null)
  const [metricsPeriod, setMetricsPeriod] = useState<'7d' | '30d' | '90d'>('30d')

  // Fetch data on mount and tab change
  useEffect(() => {
    fetchData()
  }, [activeTab, metricsPeriod])

  useEffect(() => {
    if (activeTab === 'tickets') {
      fetchTickets()
    }
  }, [filterStatus, filterPriority, pagination.page])

  const fetchData = async () => {
    setLoading(true)
    try {
      switch (activeTab) {
        case 'tickets':
          await fetchTickets()
          break
        case 'metrics':
          await fetchMetrics()
          break
      }
    } catch (error) {
      console.error('Error fetching data:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchTickets = async () => {
    try {
      const params: any = { page: pagination.page, limit: pagination.limit }
      if (filterStatus !== 'all') params.status = filterStatus
      if (filterPriority !== 'all') params.priority = filterPriority

      const response = await partnerAdminApi.support.getTickets(params)
      setTickets(response.tickets)
      setTicketsSummary(response.summary)
      setPagination(prev => ({
        ...prev,
        total: response.pagination.total,
        totalPages: response.pagination.totalPages
      }))
    } catch (error) {
      console.error('Error fetching tickets:', error)
      // Use fallback data
      setTickets(getFallbackTickets())
      setTicketsSummary(getFallbackSummary())
    }
  }

  const fetchMetrics = async () => {
    try {
      const response = await partnerAdminApi.support.getMetrics(metricsPeriod)
      // Se a API retornar zeros ou dados vazios, usar dados de exemplo
      if (!response || response.overview?.totalTickets === 0) {
        setMetrics(getFallbackMetrics())
      } else {
        setMetrics(response)
      }
    } catch (error) {
      console.error('Error fetching metrics:', error)
      setMetrics(getFallbackMetrics())
    }
  }

  const handleRefresh = async () => {
    setRefreshing(true)
    await fetchData()
    setRefreshing(false)
  }

  const handleSelectTicket = async (ticket: SupportTicket) => {
    setLoadingTicket(true)
    try {
      const details = await partnerAdminApi.support.getTicketDetails(ticket.id)
      setSelectedTicket(details)
    } catch (error) {
      console.error('Error fetching ticket details:', error)
      // Show basic ticket info
      setSelectedTicket({
        ticket: {
          id: ticket.id,
          title: ticket.title,
          description: 'Detalhes nao disponiveis',
          status: ticket.status,
          priority: ticket.priority,
          category: ticket.category,
          clientName: ticket.clientName,
          clientId: ticket.clientId,
          assignedTo: ticket.assignedTo || '',
          createdBy: '',
          createdAt: ticket.createdAt,
          lastUpdated: ticket.lastUpdated,
          estimatedResolution: ticket.estimatedResolution,
          tags: [],
          attachments: []
        },
        timeline: [],
        relatedTickets: [],
        suggestedActions: []
      })
    } finally {
      setLoadingTicket(false)
    }
  }

  const handleCreateTicket = async () => {
    if (!newTicket.title || !newTicket.description || !newTicket.clientId) return
    setCreatingTicket(true)
    try {
      await partnerAdminApi.support.createTicket(newTicket)
      setShowNewTicketModal(false)
      setNewTicket({ title: '', description: '', clientId: '', category: 'general', priority: 'medium' })
      await fetchTickets()
    } catch (error) {
      console.error('Error creating ticket:', error)
    } finally {
      setCreatingTicket(false)
    }
  }

  const handleSendReply = async () => {
    if (!selectedTicket || !replyMessage.trim()) return
    setSendingReply(true)
    try {
      await partnerAdminApi.support.replyTicket(selectedTicket.ticket.id, {
        message: replyMessage,
        isInternal: false
      })
      setReplyMessage('')
      // Refresh ticket details
      await handleSelectTicket({ ...selectedTicket.ticket } as any)
    } catch (error) {
      console.error('Error sending reply:', error)
    } finally {
      setSendingReply(false)
    }
  }

  const handleUpdateStatus = async (status: SupportTicketStatus) => {
    if (!selectedTicket) return
    try {
      await partnerAdminApi.support.updateTicketStatus(selectedTicket.ticket.id, { status })
      await handleSelectTicket({ ...selectedTicket.ticket, status } as any)
      await fetchTickets()
    } catch (error) {
      console.error('Error updating status:', error)
    }
  }

  const handleEscalate = async () => {
    if (!selectedTicket) return
    const reason = prompt('Motivo da escalacao:')
    if (!reason) return
    try {
      await partnerAdminApi.support.escalateTicket(selectedTicket.ticket.id, { reason })
      await fetchTickets()
      alert('Ticket escalado com sucesso para N2')
    } catch (error) {
      console.error('Error escalating ticket:', error)
    }
  }

  // Fallback data generators
  const getFallbackTickets = (): SupportTicket[] => [
    { id: '1', title: 'Problema com sincronizacao', status: 'open', priority: 'high', category: 'technical', clientName: 'Clinica Sao Paulo', clientId: '1', assignedTo: 'Agente 1', createdAt: new Date().toISOString(), lastUpdated: new Date().toISOString(), responseTime: 2, estimatedResolution: new Date(Date.now() + 86400000).toISOString() },
    { id: '2', title: 'Duvida sobre faturamento', status: 'in_progress', priority: 'medium', category: 'billing', clientName: 'Hospital Central', clientId: '2', assignedTo: 'Agente 2', createdAt: new Date(Date.now() - 3600000).toISOString(), lastUpdated: new Date().toISOString(), responseTime: 4, estimatedResolution: new Date(Date.now() + 172800000).toISOString() },
    { id: '3', title: 'Solicitacao de treinamento', status: 'resolved', priority: 'low', category: 'training', clientName: 'Consultorio Dr. Silva', clientId: '3', assignedTo: null, createdAt: new Date(Date.now() - 86400000).toISOString(), lastUpdated: new Date(Date.now() - 3600000).toISOString(), responseTime: 8, estimatedResolution: new Date(Date.now() - 7200000).toISOString() }
  ]

  const getFallbackSummary = (): SupportTicketsResponse['summary'] => ({
    total: 45, open: 12, inProgress: 18, resolved: 15, high: 5, medium: 28, low: 12
  })

  const getFallbackMetrics = (): SupportMetrics => {
    // Dados de exemplo baseados no período selecionado
    const multiplier = metricsPeriod === '7d' ? 0.25 : metricsPeriod === '30d' ? 1 : 3

    return {
      period: metricsPeriod,
      overview: {
        totalTickets: Math.round(47 * multiplier),
        resolvedTickets: Math.round(42 * multiplier),
        avgResolutionTime: 4.2,
        firstResponseTime: 1.5
      },
      breakdown: {
        byPriority: {
          high: Math.round(8 * multiplier),
          medium: Math.round(27 * multiplier),
          low: Math.round(12 * multiplier)
        },
        byCategory: {
          technical: Math.round(18 * multiplier),
          billing: Math.round(15 * multiplier),
          training: Math.round(9 * multiplier),
          general: Math.round(5 * multiplier)
        },
        byClient: [
          { clientName: 'Clinica Sao Paulo', ticketCount: Math.round(12 * multiplier) },
          { clientName: 'Dr. Carlos Mendes', ticketCount: Math.round(8 * multiplier) },
          { clientName: 'Clinica Vida', ticketCount: Math.round(7 * multiplier) },
          { clientName: 'Dra. Ana Costa', ticketCount: Math.round(6 * multiplier) },
          { clientName: 'Hospital Central', ticketCount: Math.round(5 * multiplier) }
        ],
        byStatus: {
          open: Math.round(3 * multiplier),
          in_progress: Math.round(2 * multiplier),
          resolved: Math.round(42 * multiplier)
        }
      },
      trends: {
        ticketVolume: [],
        resolutionTimes: [],
        satisfactionTrend: []
      },
      generatedAt: new Date().toISOString()
    }
  }


  // Helper functions
  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
    })
  }

  const getStatusBadge = (status: SupportTicketStatus) => {
    switch (status) {
      case 'open':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            <AlertCircle className="w-3 h-3" />
            Aberto
          </span>
        )
      case 'in_progress':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <Clock className="w-3 h-3" />
            Em Progresso
          </span>
        )
      case 'resolved':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircle className="w-3 h-3" />
            Resolvido
          </span>
        )
      case 'closed':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            <XCircle className="w-3 h-3" />
            Fechado
          </span>
        )
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            <AlertCircle className="w-3 h-3" />
            {status}
          </span>
        )
    }
  }

  const getPriorityBadge = (priority: SupportTicketPriority) => {
    const config: Record<SupportTicketPriority, { color: string; label: string }> = {
      low: { color: 'bg-gray-100 text-gray-800', label: 'Baixa' },
      medium: { color: 'bg-blue-100 text-blue-800', label: 'Media' },
      high: { color: 'bg-orange-100 text-orange-800', label: 'Alta' }
    }
    const cfg = config[priority]
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${cfg.color}`}>
        {cfg.label}
      </span>
    )
  }

  const getCategoryIcon = (category: SupportTicketCategory) => {
    switch (category) {
      case 'technical':
        return <HelpCircle className="w-4 h-4 text-gray-500" />
      case 'billing':
        return <FileText className="w-4 h-4 text-gray-500" />
      case 'training':
        return <Book className="w-4 h-4 text-gray-500" />
      case 'general':
      default:
        return <MessageSquare className="w-4 h-4 text-gray-500" />
    }
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <PageHeader
          title="Suporte N1"
          subtitle="Central de atendimento aos clientes do parceiro"
          actions={
            <>
              {activeTab === 'metrics' && (
                <Select
                  value={metricsPeriod}
                  onChange={e => setMetricsPeriod(e.target.value as any)}
                  options={[
                    { value: '7d', label: 'Ultimos 7 dias' },
                    { value: '30d', label: 'Ultimos 30 dias' },
                    { value: '90d', label: 'Ultimos 90 dias' }
                  ]}
                />
              )}
              <button
                onClick={() => setShowNewTicketModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all"
              >
                <Plus className="w-5 h-5" />
                Novo Ticket
              </button>
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-all"
              >
                <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
                Atualizar
              </button>
            </>
          }
        />

        {/* Tabs */}
        <div className="flex border-b border-gray-200 mt-6">
          <button
            onClick={() => setActiveTab('tickets')}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-all ${
              activeTab === 'tickets'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <MessageSquare className="w-4 h-4" />
            Tickets
          </button>
          <button
            onClick={() => setActiveTab('metrics')}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-all ${
              activeTab === 'metrics'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <BarChart3 className="w-4 h-4" />
            Metricas
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : (
        <>
          {/* Tickets Tab */}
          {activeTab === 'tickets' && (
            <div className="space-y-6">
              {/* Summary Cards */}
              {ticketsSummary && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="p-3 bg-blue-100 rounded-lg">
                        <MessageSquare className="w-6 h-6 text-blue-600" />
                      </div>
                      <span className="text-2xl font-bold text-gray-900">{ticketsSummary.open}</span>
                    </div>
                    <p className="text-sm text-gray-600">Tickets Abertos</p>
                  </div>

                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="p-3 bg-yellow-100 rounded-lg">
                        <Clock className="w-6 h-6 text-yellow-600" />
                      </div>
                      <span className="text-2xl font-bold text-gray-900">{ticketsSummary.inProgress}</span>
                    </div>
                    <p className="text-sm text-gray-600">Em Progresso</p>
                  </div>

                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="p-3 bg-green-100 rounded-lg">
                        <CheckCircle className="w-6 h-6 text-green-600" />
                      </div>
                      <span className="text-2xl font-bold text-gray-900">{ticketsSummary.resolved}</span>
                    </div>
                    <p className="text-sm text-gray-600">Resolvidos</p>
                  </div>

                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="p-3 bg-orange-100 rounded-lg">
                        <AlertTriangle className="w-6 h-6 text-orange-600" />
                      </div>
                      <span className="text-2xl font-bold text-gray-900">{ticketsSummary.high}</span>
                    </div>
                    <p className="text-sm text-gray-600">Alta Prioridade</p>
                  </div>
                </div>
              )}

              {/* Tickets List and Details */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Tickets List */}
                <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200">
                  <div className="p-6 border-b border-gray-200">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-lg font-semibold text-gray-900">Tickets</h2>
                      <div className="flex items-center gap-2">
                        <Select
                          value={filterStatus}
                          onChange={e => setFilterStatus(e.target.value as any)}
                          options={[
                            { value: 'all', label: 'Todos Status' },
                            { value: 'open', label: 'Aberto' },
                            { value: 'in_progress', label: 'Em Progresso' },
                            { value: 'resolved', label: 'Resolvido' },
                            { value: 'closed', label: 'Fechado' }
                          ]}
                        />
                        <Select
                          value={filterPriority}
                          onChange={e => setFilterPriority(e.target.value as any)}
                          options={[
                            { value: 'all', label: 'Todas Prioridades' },
                            { value: 'high', label: 'Alta' },
                            { value: 'medium', label: 'Média' },
                            { value: 'low', label: 'Baixa' }
                          ]}
                        />
                      </div>
                    </div>

                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        type="text"
                        placeholder="Buscar tickets..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-500"
                      />
                    </div>
                  </div>

                  <div className="divide-y divide-gray-200 max-h-[600px] overflow-y-auto">
                    {tickets.length === 0 ? (
                      <div className="p-8 text-center">
                        <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-500">Nenhum ticket encontrado</p>
                      </div>
                    ) : (
                      tickets.map(ticket => (
                        <div
                          key={ticket.id}
                          className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                            selectedTicket?.ticket.id === ticket.id ? 'bg-blue-50' : ''
                          }`}
                          onClick={() => handleSelectTicket(ticket)}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-3">
                              {getCategoryIcon(ticket.category)}
                              <div>
                                <div className="flex items-center gap-2 flex-wrap">
                                  {getStatusBadge(ticket.status)}
                                  {getPriorityBadge(ticket.priority)}
                                </div>
                                <h3 className="text-sm font-medium text-gray-900 mt-1">
                                  {ticket.title}
                                </h3>
                              </div>
                            </div>
                            <ChevronRight className="w-5 h-5 text-gray-400" />
                          </div>

                          <div className="flex items-center justify-between mt-2">
                            <div className="flex items-center gap-3 text-xs text-gray-500">
                              <div className="flex items-center gap-1">
                                <Building className="w-3 h-3" />
                                {ticket.clientName}
                              </div>
                              <div className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {formatDate(ticket.createdAt)}
                              </div>
                            </div>
                            {ticket.assignedTo && (
                              <span className="text-xs text-gray-500">
                                <User className="w-3 h-3 inline mr-1" />
                                {ticket.assignedTo}
                              </span>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Ticket Details */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                  {loadingTicket ? (
                    <div className="p-12 text-center">
                      <RefreshCw className="w-8 h-8 text-gray-400 mx-auto mb-2 animate-spin" />
                      <p className="text-gray-500">Carregando detalhes...</p>
                    </div>
                  ) : selectedTicket ? (
                    <>
                      <div className="p-6 border-b border-gray-200">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              {getStatusBadge(selectedTicket.ticket.status)}
                              {getPriorityBadge(selectedTicket.ticket.priority)}
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900">
                              {selectedTicket.ticket.title}
                            </h3>
                          </div>
                          <button
                            onClick={() => setSelectedTicket(null)}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>

                        <p className="text-sm text-gray-600 mb-4">
                          {selectedTicket.ticket.description}
                        </p>

                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="text-gray-500 mb-1">Cliente</p>
                            <p className="font-medium text-gray-900">{selectedTicket.ticket.clientName}</p>
                          </div>
                          <div>
                            <p className="text-gray-500 mb-1">Categoria</p>
                            <p className="font-medium text-gray-900 capitalize">{selectedTicket.ticket.category}</p>
                          </div>
                          <div>
                            <p className="text-gray-500 mb-1">Criado em</p>
                            <p className="font-medium text-gray-900">{formatDate(selectedTicket.ticket.createdAt)}</p>
                          </div>
                          {selectedTicket.ticket.assignedTo && (
                            <div>
                              <p className="text-gray-500 mb-1">Atribuido a</p>
                              <p className="font-medium text-gray-900">{selectedTicket.ticket.assignedTo}</p>
                            </div>
                          )}
                        </div>

                        {/* Quick Actions */}
                        <div className="flex gap-2 mt-4">
                          {selectedTicket.ticket.status !== 'resolved' && selectedTicket.ticket.status !== 'closed' && (
                            <>
                              <button
                                onClick={() => handleUpdateStatus('in_progress')}
                                className="flex-1 px-3 py-1.5 text-xs bg-yellow-100 text-yellow-800 rounded-lg hover:bg-yellow-200"
                              >
                                Em Progresso
                              </button>
                              <button
                                onClick={() => handleUpdateStatus('resolved')}
                                className="flex-1 px-3 py-1.5 text-xs bg-green-100 text-green-800 rounded-lg hover:bg-green-200"
                              >
                                Resolver
                              </button>
                              <button
                                onClick={handleEscalate}
                                className="flex-1 px-3 py-1.5 text-xs bg-red-100 text-red-800 rounded-lg hover:bg-red-200"
                              >
                                Escalar
                              </button>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Timeline */}
                      {selectedTicket.timeline.length > 0 && (
                        <div className="p-6 border-b border-gray-200 max-h-48 overflow-y-auto">
                          <h4 className="text-sm font-semibold text-gray-900 mb-3">Historico</h4>
                          <div className="space-y-3">
                            {selectedTicket.timeline.map(event => (
                              <div key={event.id} className="flex items-start gap-3">
                                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2" />
                                <div>
                                  <p className="text-sm text-gray-900">{event.message}</p>
                                  <p className="text-xs text-gray-500">
                                    {event.author} - {formatDate(event.timestamp)}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Reply */}
                      <div className="p-6">
                        <div className="flex items-start gap-3">
                          <textarea
                            value={replyMessage}
                            onChange={e => setReplyMessage(e.target.value)}
                            placeholder="Digite sua resposta..."
                            className="flex-1 px-3 py-2 border border-gray-200 rounded-lg resize-none focus:outline-none focus:border-blue-500"
                            rows={3}
                          />
                        </div>
                        <div className="flex items-center justify-end mt-3">
                          <button
                            onClick={handleSendReply}
                            disabled={!replyMessage.trim() || sendingReply}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all disabled:opacity-50"
                          >
                            {sendingReply ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                            Enviar
                          </button>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="p-12 text-center">
                      <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-500">Selecione um ticket para ver detalhes</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Metrics Tab */}
          {activeTab === 'metrics' && metrics && (
            <div className="space-y-6">
              {/* Overview Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <p className="text-sm text-gray-600 mb-1">Total de Tickets</p>
                  <p className="text-3xl font-bold text-gray-900">{metrics.overview.totalTickets}</p>
                </div>
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <p className="text-sm text-gray-600 mb-1">Resolvidos</p>
                  <p className="text-3xl font-bold text-green-600">{metrics.overview.resolvedTickets}</p>
                </div>
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <p className="text-sm text-gray-600 mb-1">Tempo Medio (h)</p>
                  <p className="text-3xl font-bold text-gray-900">{metrics.overview.avgResolutionTime}</p>
                </div>
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <p className="text-sm text-gray-600 mb-1">Primeira Resposta (h)</p>
                  <p className="text-3xl font-bold text-gray-900">{metrics.overview.firstResponseTime}</p>
                </div>
              </div>

              {/* Breakdown */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Por Prioridade</h3>
                  <div className="space-y-3">
                    {Object.entries(metrics.breakdown.byPriority).map(([priority, count]) => {
                      const labels: Record<string, string> = { high: 'Alta', medium: 'Media', low: 'Baixa' }
                      const colors: Record<string, string> = { high: 'text-orange-600', medium: 'text-blue-600', low: 'text-gray-600' }
                      return (
                        <div key={priority} className="flex items-center justify-between">
                          <span className={`text-sm font-medium ${colors[priority] || 'text-gray-600'}`}>
                            {labels[priority] || priority}
                          </span>
                          <span className="font-semibold text-gray-900">{count}</span>
                        </div>
                      )
                    })}
                  </div>
                </div>
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Por Categoria</h3>
                  <div className="space-y-3">
                    {Object.entries(metrics.breakdown.byCategory).map(([category, count]) => {
                      const labels: Record<string, string> = { technical: 'Tecnico', billing: 'Faturamento', training: 'Treinamento', general: 'Geral' }
                      return (
                        <div key={category} className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">{labels[category] || category}</span>
                          <span className="font-semibold text-gray-900">{count}</span>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>

              {/* Por Cliente */}
              {metrics.breakdown.byClient && metrics.breakdown.byClient.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Por Cliente</h3>
                  <div className="space-y-3">
                    {metrics.breakdown.byClient.map((client, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Building className="w-4 h-4 text-gray-400" />
                          <span className="text-sm text-gray-600">{client.clientName}</span>
                        </div>
                        <span className="font-semibold text-gray-900">{client.ticketCount} tickets</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* New Ticket Modal */}
      {showNewTicketModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-gray-900">Novo Ticket</h3>
              <button onClick={() => setShowNewTicketModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Titulo</label>
                <input
                  type="text"
                  value={newTicket.title}
                  onChange={e => setNewTicket(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-500"
                  placeholder="Resumo do problema"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descricao</label>
                <textarea
                  value={newTicket.description}
                  onChange={e => setNewTicket(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-500"
                  rows={4}
                  placeholder="Descreva o problema em detalhes"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">ID do Cliente</label>
                <input
                  type="text"
                  value={newTicket.clientId}
                  onChange={e => setNewTicket(prev => ({ ...prev, clientId: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-500"
                  placeholder="ID do cliente"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Categoria</label>
                  <Select
                    value={newTicket.category}
                    onChange={e => setNewTicket(prev => ({ ...prev, category: e.target.value as any }))}
                    options={[
                      { value: 'general', label: 'Geral' },
                      { value: 'technical', label: 'Tecnico' },
                      { value: 'billing', label: 'Faturamento' },
                      { value: 'training', label: 'Treinamento' }
                    ]}
                    fullWidth
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prioridade</label>
                  <Select
                    value={newTicket.priority}
                    onChange={e => setNewTicket(prev => ({ ...prev, priority: e.target.value as any }))}
                    options={[
                      { value: 'low', label: 'Baixa' },
                      { value: 'medium', label: 'Media' },
                      { value: 'high', label: 'Alta' }
                    ]}
                    fullWidth
                  />
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowNewTicketModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancelar
              </button>
              <button
                onClick={handleCreateTicket}
                disabled={creatingTicket || !newTicket.title || !newTicket.description || !newTicket.clientId}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {creatingTicket ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Criar Ticket
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
